import type { TicketProgress } from '@shared/schemas';
import { ticketBlockedReason } from '@/lib/ticket-meta';

/**
 * Q-3 blocked chip · «🔒 заблокирован» visual indicator с reason text on hover.
 *
 * Renders nothing если ticket not blocked (defensive · zero visual noise для
 * normal-state tickets). Consumer: Board.tsx ticket cards · Ticket.tsx header
 * meta. Replaces inline `blockedReason ? ... : null` JSX repetition.
 *
 * Schema field shipped @ 8c208a5 · helper `ticketBlockedReason` @ cfa3c1a ·
 * this component packages display affordance.
 *
 * Inline reason rendering by default · `compact` variant shows только chip
 * с reason в `title` tooltip (для tight card layouts).
 */
export interface TicketBlockedChipProps {
  ticket: Pick<TicketProgress, 'blockedReason'> | undefined;
  /** Compact mode · reason text в `title` tooltip only · saves card space. */
  compact?: boolean;
  /** Custom label prefix · defaults к «🔒 заблокирован». */
  label?: string;
}

export function TicketBlockedChip({
  ticket,
  compact = false,
  label = '🔒 заблокирован',
}: TicketBlockedChipProps) {
  const reason = ticketBlockedReason(ticket);
  if (!reason) return null;

  return (
    <span
      className={`ticket-blocked-chip${compact ? ' ticket-blocked-chip-compact' : ''}`}
      title={compact ? `${label}: ${reason}` : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        fontSize: 'var(--font-xs, 12px)',
        borderRadius: 12,
        background: 'var(--surface-warning, #fff4e0)',
        color: 'var(--ink-warning, #8c4a00)',
        border: '1px solid var(--border-warning, #ffd29a)',
        whiteSpace: compact ? 'nowrap' : 'normal',
        maxWidth: compact ? 'none' : 240,
      }}
    >
      <span aria-hidden="true">{label}</span>
      {!compact && (
        <span style={{ fontSize: 'var(--font-xs, 12px)', opacity: 0.85 }}>· {reason}</span>
      )}
    </span>
  );
}
